import { z } from 'zod';

export const webSearchTimeRangeSchema = z.enum(['day', 'month', 'year']);
export const webSearchProviderSchema = z.enum(['searxng', 'staan']);
// Routing precedence is contractual:
// provider_config > language > budget > rate_limit > circuit_open > staan_error.
export const webSearchFallbackReasonSchema = z.enum([
  'provider_config',
  'language',
  'budget',
  'rate_limit',
  'circuit_open',
  'staan_error',
]);
const webHttpUrlSchema = z.url({
  error: 'L URL web est invalide.',
  protocol: /^https?$/,
});
export const webResearchFreshnessSchema = z.enum(['any', 'day', 'month', 'year']);
export const webResearchModeSchema = z.enum(['off', 'standard', 'deep']);
export const webResearchReasonSchema = z.enum([
  'deep_research_requested',
  'explicit_web_request',
  'high_stakes',
  'internal_corpus_request',
  'recommendation',
  'stable_knowledge',
  'temporal_information',
  'url_provided',
  'verification_requested',
  'web_continuity',
  'web_toggle_default',
  'writing_only',
]);
export const webSourceKindSchema = z.enum(['official', 'primary', 'secondary', 'unknown']);
export const webResearchTargetSourceKindSchema = webSourceKindSchema.exclude(['unknown']);
export const chatRouteIntentConfidenceSchema = z.enum(['high', 'medium', 'low']);

export const chatRouteActionSchema = z.enum([
  'answer',
  'clarifyIntent',
  'createDocument',
  'createRichDocument',
]);

export const chatRouteDecisionSchema = z.object({
  action: chatRouteActionSchema,
  deliverableExplicit: z.boolean(),
  deliverableMentioned: z.boolean(),
  intentConfidence: chatRouteIntentConfidenceSchema,
  metaConversational: z.boolean(),
  research: z.object({
    freshness: webResearchFreshnessSchema,
    mode: webResearchModeSchema,
    reasons: z.array(webResearchReasonSchema).max(10),
  }).strict(),
  title: z.string().trim().min(1).max(160).nullable(),
  topic: z.string().trim().min(1).max(500),
}).strict().refine((decision) => (
  !['clarifyIntent', 'createDocument', 'createRichDocument'].includes(decision.action)
  || decision.research.mode === 'off'
), {
  error: 'La creation de document et la clarification d intention ne peuvent pas activer la recherche web.',
  path: ['research', 'mode'],
}).refine((decision) => (
  decision.action !== 'clarifyIntent' || decision.title === null
), {
  error: 'La clarification d intention ne doit pas definir de titre de document.',
  path: ['title'],
});

export const webResearchPlanSchema = z.object({
  facets: z.array(z.string().trim().min(1).max(160)).max(5),
  freshness: webResearchFreshnessSchema,
  queries: z.array(z.string().trim().min(1).max(500)).min(1).max(4),
  targetSourceTypes: z.array(webResearchTargetSourceKindSchema).max(3),
}).strict();

const webResearchActivityStatusSchema = z.enum(['completed', 'running']);

export const webResearchActivitySchema = z.discriminatedUnion('phase', [
  z.object({
    phase: z.literal('planning'),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    phase: z.literal('searching'),
    queryCount: z.number().int().nonnegative(),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    pageCount: z.number().int().nonnegative(),
    phase: z.literal('reading'),
    status: webResearchActivityStatusSchema,
  }).strict(),
  z.object({
    phase: z.literal('synthesizing'),
    status: webResearchActivityStatusSchema,
  }).strict(),
]);

export const webSearchRequestSchema = z.object({
  engines: z.array(z.string().trim().min(1)).max(5).optional(),
  language: z.string().trim().min(2).max(10).default('fr'),
  maxResults: z.number().int().min(1).max(20).default(8),
  provider: webSearchProviderSchema.optional(),
  query: z.string().trim().min(1, {
    error: 'La requete de recherche web est requise.',
  }).max(500),
  timeRange: webSearchTimeRangeSchema.optional(),
});

const nullableSearchMetadataSchema = z.preprocess(
  (value) => value === null ? undefined : value,
  z.string().trim().min(1).optional(),
);

export const webSearchResultSchema = z.object({
  domain: z.string().trim().min(1),
  engine: nullableSearchMetadataSchema,
  publishedAt: nullableSearchMetadataSchema,
  rank: z.number().int().positive(),
  snippet: z.string(),
  title: z.string(),
  url: webHttpUrlSchema,
});

export const webSearchResponseSchema = z.object({
  cached: z.boolean(),
  effectiveProvider: webSearchProviderSchema.optional(),
  fallbackReason: webSearchFallbackReasonSchema.optional(),
  freshnessFiltered: z.boolean().optional(),
  timeRangeRelaxed: z.boolean().optional(),
  latencyMs: z.number().int().nonnegative(),
  query: z.string(),
  queryTruncated: z.boolean().optional(),
  requestedTimeRange: webSearchTimeRangeSchema.optional(),
  results: z.array(webSearchResultSchema),
  selectedProvider: webSearchProviderSchema.optional(),
  staanRequestCount: z.number().int().nonnegative().optional(),
});

export const webFetchModeSchema = z.enum(['auto', 'http', 'dynamic', 'stealth']);
export const webFetchResolvedModeSchema = z.enum(['http', 'dynamic', 'stealth']);

export const webFetchRequestSchema = z.object({
  maxChars: z.number().int().min(500).max(50_000).default(12_000),
  mode: webFetchModeSchema.default('auto'),
  url: webHttpUrlSchema.max(2048),
});

export const webFetchResponseSchema = z.object({
  author: z.string().nullable().optional(),
  cached: z.boolean(),
  contentType: z.string().nullable(),
  fetchMode: webFetchResolvedModeSchema,
  finalUrl: webHttpUrlSchema,
  latencyMs: z.number().int().nonnegative(),
  publishedAt: z.string().trim().min(1).nullable().optional(),
  text: z.string(),
  title: z.string().nullable(),
  truncated: z.boolean(),
  url: webHttpUrlSchema,
});

export const updateConversationWebSearchSchema = z.object({
  webSearchEnabled: z.boolean(),
});

export type WebFetchModeDTO = z.infer<typeof webFetchModeSchema>;
export type WebFetchRequestDTO = z.infer<typeof webFetchRequestSchema>;
export type WebFetchResponseDTO = z.infer<typeof webFetchResponseSchema>;
export type ChatRouteActionDTO = z.infer<typeof chatRouteActionSchema>;
export type ChatRouteDecisionDTO = z.infer<typeof chatRouteDecisionSchema>;
export type WebResearchActivityDTO = z.infer<typeof webResearchActivitySchema>;
export type WebResearchFreshnessDTO = z.infer<typeof webResearchFreshnessSchema>;
export type WebResearchModeDTO = z.infer<typeof webResearchModeSchema>;
export type WebResearchPlanDTO = z.infer<typeof webResearchPlanSchema>;
export type WebResearchReasonDTO = z.infer<typeof webResearchReasonSchema>;
export type WebSearchRequestDTO = z.infer<typeof webSearchRequestSchema>;
export type WebSearchFallbackReasonDTO = z.infer<typeof webSearchFallbackReasonSchema>;
export type WebSearchProviderDTO = z.infer<typeof webSearchProviderSchema>;
export type WebSearchTimeRangeDTO = z.infer<typeof webSearchTimeRangeSchema>;
export type WebSearchResponseDTO = z.infer<typeof webSearchResponseSchema>;
export type WebSearchResultDTO = z.infer<typeof webSearchResultSchema>;
export type WebSourceKindDTO = z.infer<typeof webSourceKindSchema>;
export type UpdateConversationWebSearchDTO = z.infer<typeof updateConversationWebSearchSchema>;
